/* eslint-disable camelcase */
import { ReportGrid } from "@app-components/ReportGrid";
import { TabularData } from "@app-components/TabularData";
import { useBroadcastChannel } from "@app-context/broadcastChannels/context";
import { useNetworkCalls } from "@app-context/networkCalls/context";
import { APISources } from "@app-context/networkCalls/types";
import { useReports } from "@app-context/reports/context";
import { useRefCallback } from "@enfusion-ui/hooks";
import {
  WebReportQuery,
  WebReportTableSync,
  WebReportTableSyncRow,
} from "@enfusion-ui/types";
import {
  CenterContent,
  ContentMessage,
  SecondaryTabList,
  Spinner,
  VisibleIf,
} from "@enfusion-ui/web-components";
import {
  errorToast,
  ReportDataChangeArgs,
  useAuth,
  useThisTab,
} from "@enfusion-ui/web-core";
import { faPaperPlane } from "@fortawesome/free-solid-svg-icons";
import {
  CellClickedEvent,
  ColDef,
  GridReadyEvent,
  RowGroupOpenedEvent,
} from "ag-grid-community";
import * as React from "react";
import ReactPrismEditor from "react-prism-editor";
import styled from "styled-components";
import { v4 as uuidv4 } from "uuid";

import {
  InspectorReportConfig,
  useInspectorContext,
} from "../../context/inspector/context";
import { SocketData } from "../../types/socket";
import ReportLoadingStatus from "../ReportLoadingStatus";
import {
  ContentProp,
  Line,
  ResponseContainer,
  ResponseTab,
} from "./InspectorContent";

const streamRawColumnDefs = [
  {
    colId: "direction",
    field: "direction",
    cellRenderer: "directionCellRenderer",
    width: 30,
  },
  {
    colId: "data",
    field: "data",
    headerName: "Data",
    width: 1200,
  },
  {
    colId: "length",
    field: "length",
    headerName: "Length",
    width: 80,
  },
  {
    colId: "timestamp",
    field: "timestamp",
    headerName: "Timestamp",
    width: 120,
  },
];

const ResponseTabs = [
  {
    label: "Raw data",
    key: "raw-data",
  },
  {
    label: "Tabular Data",
    key: "tabular-data",
  },
];
export type PositionData = {
  columnDefs: ColDef[];
  rowData: WebReportTableSyncRow[];
};

export type StreamMessageTableSyncCell = {
  value: any;
  status?: "Error" | "Warning" | "Pending";
  exception?: string;
};
export type StreamData = {
  columnDefs: ColDef[];
  rowData: SocketData[] | null;
};

export type PositionFinderRowData = WebReportTableSyncRow & {
  __row_idx: string;
};

const RawSection = styled.div<{ cellClicked: boolean }>`
  width: 100%;
  flex: 1;
  height: ${({ cellClicked }) =>
    cellClicked ? "calc(50% - 1px - var(--spacing))" : "100%"};
  overflow: auto;

  .module-theme-tomorrow {
    margin-bottom: -10px;
  }
`;

export const ReportContent: React.FC<ContentProp> = ({ setParamsOpen }) => {
  const { fetchCall: fetchCallInternal } = useNetworkCalls();

  const [loading, setLoading] = React.useState(false);
  const [reportLoading, setReportLoading] = React.useState(false);
  const [response, setResponse] = React.useState<
    WebReportTableSync | string | null
  >(null);
  const [gridData, setGridData] = React.useState<PositionData>({
    columnDefs: [],
    rowData: [],
  });
  const [streamGridData, setStreamGridData] = React.useState<StreamData>({
    columnDefs: streamRawColumnDefs,
    rowData: null,
  });
  const [error, setError] = React.useState<Error | null>(null);
  const [selectedMainTab, setSelectedMainTab] =
    React.useState<string>("tabular-data");
  const [cellClickedData, setCellClickedData] = React.useState("");
  const [masterDetail, setMasterDetail] = React.useState(false);
  const closeFirst = React.useRef(false);
  const {
    config: configBase,
    getSecureToken,
    apiSource,
    environment,
  } = useInspectorContext();
  const config = configBase as InspectorReportConfig;
  const { id } = useThisTab();
  const loadReportId = uuidv4();
  const {
    dataStore,
    metaStore,
    messages,
    reportId,
    loadReport,
    subscribeToReportChange,
    openDetailTable,
    initializeReports,
  } = useReports();
  const { user } = useAuth();
  const [metadata, setMetadata] = React.useState(
    metaStore[reportId] ?? undefined
  );

  const signalRef = React.useRef(new AbortController());
  const gridApiRef = React.useRef<GridReadyEvent | null>(null);
  const gridOptions = dataStore[reportId];
  const childEvent = React.useRef<RowGroupOpenedEvent>();

  React.useEffect(() => {
    setGridData({
      columnDefs: [],
      rowData: [],
    });
    setParamsOpen(true);
  }, [apiSource]);

  React.useEffect(() => {
    if (gridOptions && !response) {
      getStreamReport();
    } else {
      setGridData({
        columnDefs: [],
        rowData: [],
      });
    }
    setMetadata(metaStore[reportId]);
  }, [gridOptions]);

  React.useEffect(() => {
    const detailGridRowId = childEvent.current?.data.__row_id;
    const detailGridApi = childEvent.current?.api.getDetailGridInfo(
      `detail_${detailGridRowId}`
    )?.api;
    detailGridApi?.updateGridOptions({
      columnDefs: dataStore[detailGridRowId]?.columnDefs,
      rowData:
        dataStore[detailGridRowId]?.rows?.map((i: any, idx: number) => ({
          ...i.columnValues,
          __row_id: dataStore[detailGridRowId]?.rows[idx]?.rowId,
          __row_idx: idx,
        })) || [],
    });
  }, [childEvent.current, dataStore]);

  React.useEffect(() => {
    if (messages?.length) {
      const rowData = messages.map(
        (i, idx) =>
          ({
            ...i,
            __row_id: uuidv4(),
            __row_idx: idx,
          } as any)
      );

      setStreamGridData((prev) => ({
        ...prev,
        rowData,
      }));
    }
  }, [messages]);

  React.useEffect(() => {
    return subscribeToReportChange(reportId, handleReportChange);
  }, [reportId]);

  const onGridReady = useRefCallback(
    (event: GridReadyEvent) => {
      gridApiRef.current = event;
    },
    [gridOptions]
  );

  const handleReportChange = useRefCallback(
    (args: ReportDataChangeArgs) => {
      gridApiRef.current?.api.applyTransactionAsync(args.change);
      if (args.totalsRow) {
        gridApiRef.current?.api.setPinnedTopRowData([args.totalsRow]);
      }
    },
    [gridApiRef]
  );

  const onRowGroupOpened = useRefCallback(
    (event: RowGroupOpenedEvent) => {
      childEvent.current = event;
      if (event.expanded) {
        const rowId = event.node.data.__row_id;
        openDetailTable({
          destination: metadata?.destination || `/report-feed/report/${rowId}`,
          parentTableId: event?.context?.tableId ?? "",
          row: rowId,
          tableId: rowId,
        });
      }
    },
    [metadata, childEvent.current]
  );

  const handleSend = useRefCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setResponse(null);

      const secureToken = await getSecureToken();
      if (apiSource === APISources.CORE && !secureToken) {
        return;
      }
      if (apiSource === APISources.INTERNAL && !user) {
        errorToast(
          "Failed to subscribe.",
          "Please login to continue internally."
        );
        return;
      }
      signalRef.current = new AbortController();
      setParamsOpen(false);

      if (config.path) {
        initializeReports(
          apiSource === APISources.CORE ? secureToken : undefined
        );
        setReportLoading(true);
        if (apiSource === APISources.INTERNAL) {
          const res = await fetchCallInternal<WebReportQuery>(
            environment,
            `/internal/api/reports/query?report=${encodeURIComponent(
              config.path
            )}`
          );
          loadReport({
            reportId: `${loadReportId}`,
            name: `${config.path}`,
            path: `${config.path}`,
            params: { tableId: loadReportId },
            reportQuery: res[0],
            closeFirst: closeFirst.current,
          });
        } else {
          loadReport({
            reportId: `${loadReportId}`,
            name: `${config.path}`,
            path: `${config.path}`,
            params: { tableId: loadReportId },
            closeFirst: closeFirst.current,
          });
        }
        closeFirst.current = true;
      } else {
        errorToast("Please specify a report path to subscribe.");
      }
    } catch (err: any) {
      setError(err);
      errorToast("Error occurred ", err?.message);
    } finally {
      setLoading(false);
    }
  }, [config, apiSource, user, loadReportId, closeFirst.current]);

  const onCellClicked = useRefCallback((event: CellClickedEvent) => {
    setCellClickedData(event.data.data);
  }, []);

  const getStreamReport = useRefCallback(() => {
    setReportLoading(false);
    const { rows, metadata, totalsRow, columnDefs } = gridOptions;
    const resp: any = {
      tableMetadata: metadata,
      rows: rows,
      totalRows: totalsRow,
    };

    if (metadata.hasChildren) setMasterDetail(true);
    setResponse(resp);
    const rowData = rows?.map(
      (i, idx) =>
        ({
          ...i.columnValues,
          __row_id: gridOptions.rows[idx].rowId,
          __row_idx: idx,
        } as any)
    );
    setGridData(() => ({
      columnDefs,
      rowData,
    }));
  }, [gridOptions]);

  useBroadcastChannel("inspector-channel", (msg) => {
    const [msgId, type] = msg.split("<=>");
    if (msgId === id) {
      switch (type) {
        case "send":
          handleSend();
          break;
        case "cancel":
          signalRef.current.abort();
          setLoading(true);
          setError(null);
          setResponse(null);
          break;
        default:
          break;
      }
    }
  });

  return (
    <>
      <Line />
      <ResponseTab>
        {loading ? (
          <CenterContent>
            <Spinner />
          </CenterContent>
        ) : error ? (
          <CenterContent>
            <TabularData colDefs={[]} rowData={[]} />
          </CenterContent>
        ) : streamGridData.rowData ? (
          <>
            <SecondaryTabList
              value={selectedMainTab}
              onSelect={setSelectedMainTab}
              tabs={ResponseTabs}
            />
            <ResponseContainer>
              <VisibleIf visible={selectedMainTab === "raw-data"}>
                <RawSection cellClicked={!!cellClickedData}>
                  <TabularData
                    colDefs={streamGridData.columnDefs}
                    rowData={streamGridData.rowData}
                    onCellClicked={onCellClicked}
                  />
                </RawSection>
                {cellClickedData && (
                  <>
                    <Line style={{ marginTop: "var(--spacing)" }} />
                    <RawSection cellClicked={!!cellClickedData}>
                      <ReactPrismEditor
                        language="json"
                        theme="tomorrow"
                        code={JSON.stringify(
                          JSON.parse(cellClickedData),
                          null,
                          2
                        )}
                        lineNumber
                        readOnly
                        clipboard
                      />
                    </RawSection>
                  </>
                )}
              </VisibleIf>
              <VisibleIf visible={selectedMainTab === "tabular-data"}>
                {reportLoading && metaStore[reportId]?.progressSteps ? (
                  <CenterContent>
                    <ReportLoadingStatus
                      steps={metaStore[reportId].progressSteps}
                    />
                  </CenterContent>
                ) : reportLoading ? (
                  <CenterContent>
                    <Spinner />
                  </CenterContent>
                ) : (
                  <ReportGrid
                    colDefs={gridData.columnDefs}
                    rowData={gridData.rowData || []}
                    onGridReady={onGridReady}
                    onRowGroupOpened={onRowGroupOpened}
                    masterDetail={masterDetail}
                  />
                )}
              </VisibleIf>
            </ResponseContainer>
          </>
        ) : (
          <CenterContent>
            <ContentMessage icon={faPaperPlane} message="Subscribe Content" />
          </CenterContent>
        )}
      </ResponseTab>
    </>
  );
};
